import { delay, put } from 'redux-saga/effects';

import { WEEK, MONTHS } from '../constants/languages';
import { DAY_IN_MILLISECONDS, INTERVAL_NAMES } from '../constants/tasks';

import * as localStorageService from '../services/localStorage';

const language = localStorageService.getLanguage();

export const updateObject = (oldObject, updatedProperties) => {
  return { ...oldObject, ...updatedProperties };
};

export const sortTasksArrayByDateFunction = (a, b) => {
  const now = new Date();
  const aDate = new Date(a.date || a.startDate || now).getTime();
  const bDate = new Date(b.date || b.startDate || now).getTime();

  if (aDate !== bDate) {
    return aDate - bDate;
  }

  if (a.importance !== b.importance) {
    return b.importance - a.importance;
  }

  // to sort tasks with time first
  const aTime = a.time || '23:59';
  const bTime = b.time || '23:59';
  const aTimeNumber = parseInt(aTime.split(':')[0], 10) * 60 * 60 + parseInt(aTime.split(':')[1], 10) * 60;
  const bTimeNumber = parseInt(bTime.split(':')[0], 10) * 60 * 60 + parseInt(bTime.split(':')[1], 10) * 60;

  if (aTimeNumber !== bTimeNumber) {
    return aTimeNumber - bTimeNumber;
  }

  return a.title.localeCompare(b.title);
};

export const sortTasksArrayByDate = (tasks = []) => {
  return tasks.sort(sortTasksArrayByDateFunction);
};

export const toLocalMidnight = (dateParam = new Date()) => {
  const dateStr = typeof dateParam === 'string' ? dateParam : new Date(dateParam).toISOString();
  const date = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) {
    return new Date(year || new Date().getFullYear(), (month || new Date().getMonth()) - 1, day || new Date().getDate(), 0, 0, 0);
  }
  return new Date(year, month - 1, day, 0, 0, 0);
};

export const formatDate = (date = new Date()) => {
  const d = new Date(typeof date === 'string' ? toLocalMidnight(date) : new Date(date));
  let month = `${d.getMonth() + 1}`;
  let day = `${d.getDate()}`;
  const year = d.getFullYear();

  if (month.length < 2) month = `0${month}`;
  if (day.length < 2) day = `0${day}`;

  return [year, month, day].join('-');
};

// export const formatTime = (date = new Date()) => {
//   const d = new Date(date);
//   let hours = d.getHours().toString();
//   let minutes = d.getMinutes().toString();
//   if (hours.length < 2) hours = `0${hours}`;
//   if (minutes.length < 2) minutes = `0${minutes}`;
//   return [hours, minutes].join(':');
// };

export const getDateFromDateString = (date = '') => {
  const dateString = date.includes('T') ? date.split('T')[0] : date;
  if (!dateString) {
    return '';
  }
  const dateArray = dateString.split('-');
  const year = Number(dateArray[0] || 0);
  const month = Number(dateArray[1] || 0);
  const day = Number(dateArray[2] || 0);
  if (!year || !month || !day) {
    return formatDate(date);
  }
  return formatDate(new Date(new Date(new Date(new Date(new Date(date).setFullYear(year)).setMonth(month - 1)).setDate(day)).setHours(0)));
};

export const getDateFromFormatDateString = (date = '') => {
  if (!date) {
    return new Date();
  }
  const dateArray = date.split('-');
  const year = Number(dateArray[0] || 0);
  const month = Number(dateArray[1] || 0);
  const day = Number(dateArray[2] || 0);
  if (!year || !month || !day) {
    return new Date();
  }
  return new Date(new Date(new Date(new Date(new Date(date).setFullYear(year)).setMonth(month - 1)).setDate(day)).setHours(0));
};

export const getTimeFromDateString = (date = '', timeZone = 0) => {
  const timeString = date.includes('T') ? date.split('T')[1] : date;
  if (!timeString) {
    return '';
  }
  const timeArray = timeString.split(':');
  const hoursNumber = Number(timeArray[0]) + timeZone;
  let hours = String(Math.abs(hoursNumber));
  if (hoursNumber <= 0) {
    hours = String(Math.abs(hoursNumber + 24));
  }
  if (hoursNumber >= 24) {
    hours = String(Math.abs(hoursNumber - 24));
  }
  let minutes = timeArray[1];
  if (!hours || !minutes) {
    return '';
  }
  // float timeZone workaround (ex. 5.5)
  if (hours.includes('.')) {
    const minutesFloatNumber = Number(`0.${hours.split('.')[1]}`) || '0';
    minutes = parseFloat(String(Number(minutes) + Number(minutesFloatNumber) * 60)).toFixed(0);
    if (minutes >= 60) {
      hours = String(Number(hours.split('.')[0]) + Math.floor(minutes / 60)) || '0';
      minutes = String(minutes % 60);
    } else {
      hours = hours.split('.')[0] || '0';
    }
  }
  hours = String(Number(hours) % 24);
  return `${hours.length < 2 ? `0${hours}` : hours}:${minutes.length < 2 ? `0${minutes}` : minutes}`;
};

export const formatDateForDayPickerInput = (date = formatDate()) => {
  const d = date.split('-');
  const year = d[0];
  const month = d[1];
  const day = d[2];

  return [day, month, year].join('.');
};

export const formatMonthToMM = (date = new Date()) => {
  const d = new Date(date);
  let month = `${d.getMonth() + 1}`;
  if (month.length < 2) month = `0${month}`;
  return month;
};

export const formatMonthToMMYY = (date = new Date()) => {
  const d = new Date(date);
  let month = `${d.getMonth() + 1}`;
  if (month.length < 2) month = `0${month}`;
  const year = `${d.getFullYear()}`.slice(-2);
  return `${month}.${year}`;
};

export const formatMonthToDDMM = (date = new Date()) => {
  const d = new Date(date);
  let day = `${d.getDate()}`;
  if (day.length < 2) day = `0${day}`;
  let month = `${d.getMonth() + 1}`;
  if (month.length < 2) month = `0${month}`;
  return `${day}.${month}`;
};

export const getTimeZoneOffset = (date = new Date()) => {
  const timezoneOffset = new Date(date).getTimezoneOffset();
  return timezoneOffset === 0 ? 0 : timezoneOffset / -60;
};

export const formatDay = (date = new Date()) => {
  const dayNumber = new Date(date).getDay();
  return WEEK[language][dayNumber];
};

export const formatMonth = (date = new Date()) => {
  const monthNumber = new Date(date).getMonth();
  return MONTHS[language][monthNumber];
};

export const formatDateToDD = (date = new Date()) => {
  return new Date(date).getDate() < 10 ? `0${new Date(date).getDate()}` : `${new Date(date).getDate()}`;
};

export const getMonthByDateRange = (firstDate = new Date(), lastDate = new Date()) => {
  const month1 = formatMonth(new Date(firstDate));
  const month2 = formatMonth(new Date(lastDate));

  if (month1 === month2) {
    return month1;
  }
  return `${month1} - ${month2}`;
};

export const isDateInRange = (date = new Date(), firstDate = new Date(), lastDate = new Date()) => {
  const dateTimeStamp = new Date(toLocalMidnight(date)).getTime();
  const firstDateTimeStamp = new Date(firstDate).getTime();
  const lastDateTimeStamp = new Date(lastDate).getTime();
  return dateTimeStamp >= firstDateTimeStamp && dateTimeStamp <= lastDateTimeStamp;
};

export const getFrequencyByWeek = (daysOfWeek = []) => {
  return `0 12 * * ${daysOfWeek}`;
};

export const getFrequencyByMonth = (daysOfMonth = []) => {
  return `0 12 ${daysOfMonth} * *`;
};

export const getDaysOfWeekByFrequency = (frequency = '') => {
  const frequencyArray = frequency.split(' ');
  // const min = frequencyArray[0];
  // const hour = frequencyArray[1];
  // const day = frequencyArray[2];
  // const month = frequencyArray[3];
  const daysOfWeek = frequencyArray[4];
  return daysOfWeek && daysOfWeek !== '*' ? daysOfWeek.split(',') : null;
};

export const getDaysOfMonthByFrequency = (frequency = '') => {
  const frequencyArray = frequency.split(' ');
  // const min = frequencyArray[0];
  // const hour = frequencyArray[1];
  const day = frequencyArray[2];
  // const month = frequencyArray[3];
  // const daysOfWeek = frequencyArray[4];
  return day && day !== '*' ? day.split(',') : null;
};

export const getLastDayOfMonth = (date = new Date()) => {
  return new Date(new Date(date).getFullYear(), new Date(date).getMonth() + 1, 0).getDate();
};

export const getDatesByFrequency = (startDate = formatDate(), endDate = formatDate(), frequency = '', intervalName = '', intervalValue = 0) => {
  const allDaysArray = [];
  const frequencyArray = frequency.split(' ');
  if (!frequency || !intervalName || !intervalValue) {
    return allDaysArray;
  }

  switch (intervalName) {
    case INTERVAL_NAMES.DAY: {
      // const min = frequencyArray[0];
      // const hour = frequencyArray[1];
      const day = frequencyArray[2];
      const month = frequencyArray[3];
      // const week = frequencyArray[4];
      const year = frequencyArray[5];
      if (!day || !month || !year) {
        return allDaysArray;
      }

      const startDay = `${year}-${month}-${day}`;

      for (let i = Date.parse(startDay); i <= Date.parse(endDate); i += DAY_IN_MILLISECONDS * intervalValue) {
        allDaysArray.push(new Date(i));
      }

      return allDaysArray;
    }

    case INTERVAL_NAMES.WEEK: {
      // const min = frequencyArray[0];
      // const hour = frequencyArray[1];
      // const day = frequencyArray[2];
      // const month = frequencyArray[3];
      const week = frequencyArray[4];
      // const year = frequencyArray[5];
      if (!week) {
        return allDaysArray;
      }

      let weekArray = week
        .split(',')
        .filter((day) => {
          return day;
        })
        .map((day) => {
          return Number(day);
        });
      weekArray = weekArray.sort((a, b) => {
        return a - b;
      });

      let skipWeek = false;
      let intervalCounter = intervalValue;
      for (let i = Date.parse(startDate); i <= Date.parse(endDate); i += DAY_IN_MILLISECONDS) {
        const day = new Date(i).getDay();
        if (weekArray.includes(day) && !skipWeek) {
          allDaysArray.push(new Date(i));
        }
        // todo weekStartOption
        if (day === 0 && allDaysArray.length > 0) {
          skipWeek = true;
          intervalCounter -= 1;
          if (intervalCounter === 0) {
            skipWeek = false;
            intervalCounter = intervalValue;
          }
        }
      }

      return allDaysArray;
    }
    case INTERVAL_NAMES.MONTH: {
      // const min = frequencyArray[0];
      // const hour = frequencyArray[1];
      const day = frequencyArray[2];
      // const month = frequencyArray[3];
      // const week = frequencyArray[4];
      // const year = frequencyArray[5];
      if (!day) {
        return allDaysArray;
      }

      let daysArray = day
        .split(',')
        .filter((d) => {
          return d;
        })
        .map((d) => {
          return Number(d);
        });
      daysArray = daysArray.sort((a, b) => {
        return a - b;
      });

      let skipMonth = false;
      let intervalCounter = intervalValue;
      for (let i = Date.parse(startDate); i <= Date.parse(endDate); i += DAY_IN_MILLISECONDS) {
        const lastDay = getLastDayOfMonth(new Date(i));
        const d = new Date(i).getDate();
        if (daysArray.includes(d) && !skipMonth) {
          allDaysArray.push(new Date(i));
        }
        if (d === lastDay && allDaysArray.length > 0) {
          skipMonth = true;
          intervalCounter -= 1;
          if (intervalCounter === 0) {
            skipMonth = false;
            intervalCounter = intervalValue;
          }
        }
      }

      return allDaysArray;
    }
    default:
      return allDaysArray;
  }
};

export const compareTasksByDate = (otherArray) => {
  return (current) => {
    return (
      otherArray.filter((other) => {
        return formatDate(other.date) === formatDate(current.date);
      }).length === 0
    );
  };
};

export const getMidnight = (date = new Date()) => {
  return new Date(toLocalMidnight(date)).setHours(0, 0, 0, 0);
};

export const isDayPassed = (date) => {
  if (!date) {
    return true;
  }
  return new Date(date).getTime() + 24 * 60 * 60 * 1000 <= new Date().getTime();
};

export const checkTitle = (title = '') => {
  return /\S/.test(title);
};

export const truncate = (str) => {
  return str.length > 120 ? `${str.slice(0, 119)}…` : str;
};
export const truncateName = (str) => {
  return str.length > 23 ? `${str.slice(0, 22)}…` : str;
};
export const truncateGlobalTask = (str) => {
  return str.length > 35 ? `${str.slice(0, 34)}…` : str;
};

export const minutesToDecimal = (minutes = 0) => {
  return (minutes * 100) / 60;
};

export const decimalToMinutes = (decimal = 0) => {
  return (decimal * 60) / 100;
};

export const stringifyDecimalMinutes = (minutes = '0') => {
  return minutes.length < 2 ? `${minutes}0` : minutes;
};

export const getChartColor = (percent = 0) => {
  if (percent < 1) return 'rgb(240,240,240)';
  if (percent >= 1 && percent < 30) return 'rgb(207,44,34)';
  if (percent >= 30 && percent < 70) return 'rgb(255,188,36)';
  if (percent >= 70) return 'rgb(65,200,74)';
  return 'rgb(240,240,240)';
};

export const shallowCompare = (obj1 = {}, obj2 = {}) => {
  return (
    Object.keys(obj1).length === Object.keys(obj2).length
    && Object.keys(obj1).every((key) => {
      return Object.prototype.hasOwnProperty.call(obj2, key) && obj1[key] === obj2[key];
    })
  );
};

export function* delayGenerator(delayMS = 0, action = () => {}) {
  yield delay(delayMS || 0);
  yield put(action());
}
